import {
  select,
  scaleTime,
  scaleLinear,
  axisBottom,
  axisLeft,
  line,
  curveStepAfter,
  curveLinear,
  brushX,
  pointer,
} from "d3";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { findLeftIndex, findClosestPoint, generateChartTicks } from "../../utils/utility";
import { CHART_CONSTANTS } from "../../utils/constants";
import {
  CHART_COLORS,
  CHART_COLORS_DARK,
  FONT_FAMILY,
  MARGIN,
  SERIES_CONFIG,
  Y_AXIS_PADDING_MULTIPLIER,
  X_AXIS_LABEL_OFFSET,
  Y_AXIS_LABEL_OFFSET,
  TOOLTIP_BOTTOM_PADDING,
  BACKGROUND_BORDER_RADIUS,
  EVENT_ABBREVIATIONS,
  EVENT_COLORS,
} from "./constants";
import { buildTooltipHTML } from "./utils";

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Pure D3 rendering function for the LineBarComposedChart.
 * Draws axes, series lines, crosshair, tooltip interactions, brush zoom, and shift+drag pan.
 *
 * @param {object} params
 * @param {HTMLElement} params.container - The chart container DOM element
 * @param {SVGElement} params.svgEl - The SVG DOM element
 * @param {HTMLElement} params.tooltipEl - The tooltip DOM element
 * @param {object} params.allSeriesData - All series data keyed by SERIES_CONFIG.key
 * @param {object} params.visibility - Map of series name -> boolean visibility
 * @param {object} params.fullExtent - { minX, maxX, maxY }
 * @param {object|null} params.zoomExtremes - { min, max, source } or null
 * @param {React.MutableRefObject} params.isSyncingRef - Ref to prevent zoom feedback loops
 * @param {Function|null} params.onZoomChange - Callback for zoom changes
 * @param {string} params.clipPathId - Unique ID for the SVG clip path
 * @param {React.MutableRefObject} params.panCleanupRef - Ref to store pan listener cleanup
 * @param {boolean} params.isDark - Whether dark theme is active
 * @param {string} params.siteTimezone - IANA timezone string for the site
 */
export default function renderChart({
  container,
  svgEl,
  tooltipEl,
  allSeriesData,
  visibility,
  fullExtent,
  zoomExtremes,
  isSyncingRef,
  onZoomChange,
  clipPathId,
  panCleanupRef,
  isDark = false,
  siteTimezone,
  events = [],
  dateRange: selectedDateRange = null,
}) {
  const colors = isDark ? CHART_COLORS_DARK : CHART_COLORS;
  if (!container || !svgEl) return;

  const width = container.clientWidth;
  const height = container.clientHeight;
  if (width <= 0 || height <= 0) return;

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;
  if (innerWidth <= 0 || innerHeight <= 0) return;

  const svg = select(svgEl);
  svg.selectAll("*").remove();
  svg.attr("width", width).attr("height", height);

  // Background
  svg
    .append("rect")
    .attr("width", width)
    .attr("height", height)
    .attr("fill", colors.gridBackground)
    .attr("rx", BACKGROUND_BORDER_RADIUS);

  const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

  // Scales
  const xScale = scaleTime().range([0, innerWidth]);
  const yScale = scaleLinear().range([innerHeight, 0]);

  // Determine x domain from zoom, selected date range, or full extent
  let xDomainMin = fullExtent.minX;
  let xDomainMax = fullExtent.maxX;

  // Prefer selectedDateRange if available (it's always the correct range)
  if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
    xDomainMin = selectedDateRange[0].valueOf();
    xDomainMax = selectedDateRange[1].valueOf();
  }

  xScale.domain([zoomExtremes ? zoomExtremes.min : xDomainMin, zoomExtremes ? zoomExtremes.max : xDomainMax]);

  // Compute visible y max
  let visibleMaxY = 0;
  SERIES_CONFIG.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const d = allSeriesData[cfg.key];
    if (!d) return;
    d.forEach(point => {
      if (
        point[0] >= xScale.domain()[0] &&
        point[0] <= xScale.domain()[1] &&
        point[1] !== null &&
        point[1] !== undefined &&
        point[1] > visibleMaxY
      ) {
        visibleMaxY = point[1];
      }
    });
  });
  yScale.domain([0, visibleMaxY * Y_AXIS_PADDING_MULTIPLIER || 1]);

  // Clip path
  svg
    .append("defs")
    .append("clipPath")
    .attr("id", clipPathId)
    .append("rect")
    .attr("width", innerWidth)
    .attr("height", innerHeight);

  // Grid lines (Y)
  g.append("g")
    .attr("class", "grid-y")
    .call(axisLeft(yScale).tickSize(-innerWidth).tickFormat(""))
    .selectAll("line")
    .attr("stroke", colors.gridLines)
    .attr("stroke-dasharray", "4,4");
  g.select(".grid-y").select(".domain").remove();

  // Determine X-axis ticks and format based on the visible x domain (respects zoom)
  const [visibleXMin, visibleXMax] = xScale.domain();
  const { tickValues, tickFormat } = generateChartTicks(visibleXMin.getTime(), visibleXMax.getTime(), siteTimezone);

  const xAxisGen = axisBottom(xScale).tickValues(tickValues).tickFormat(tickFormat);

  const xAxis = g.append("g").attr("transform", `translate(0,${innerHeight})`).call(xAxisGen);
  xAxis
    .selectAll("text")
    .attr("fill", colors.textSecondary)
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY)
    .attr("text-anchor", "middle");
  xAxis.selectAll("line").attr("stroke", colors.gridLines);
  xAxis.select(".domain").attr("stroke", colors.gridLines);

  // X Axis title
  g.append("text")
    .attr("x", innerWidth / 2)
    .attr("y", innerHeight + X_AXIS_LABEL_OFFSET)
    .attr("text-anchor", "middle")
    .attr("fill", colors.textPrimary)
    .style("font-weight", "500")
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY)
    .text("Time");

  // Determine Y-axis format and label based on max value
  const maxValue = fullExtent.maxY;
  let yAxisLabel = "Power (W)";
  let yAxisTickFormat;

  if (maxValue >= 1000) {
    // Show in kW (1-10 kW range)
    yAxisLabel = "Power (kW)";
    yAxisTickFormat = d => (d / 1000).toFixed(1);
  } else {
    // Show in W with 100W intervals
    yAxisTickFormat = d => d.toLocaleString();
  }

  // Y Axis
  const yAxis = g.append("g").call(
    axisLeft(yScale)
      .ticks(maxValue >= 1000 ? 10 : Math.ceil(maxValue / 100))
      .tickFormat(yAxisTickFormat)
  );
  yAxis
    .selectAll("text")
    .attr("fill", colors.textSecondary)
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY);
  yAxis.selectAll("line").attr("stroke", colors.gridLines);
  yAxis.select(".domain").attr("stroke", colors.gridLines);

  // Y Axis title
  g.append("text")
    .attr("transform", "rotate(-90)")
    .attr("x", -innerHeight / 2)
    .attr("y", -Y_AXIS_LABEL_OFFSET)
    .attr("text-anchor", "middle")
    .attr("fill", colors.textPrimary)
    .style("font-weight", "500")
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY)
    .text(yAxisLabel);

  // Chart area with clip
  const chartArea = g.append("g").attr("clip-path", `url(#${clipPathId})`);

  // Sort series: bars first (so lines draw on top), then by zIndex
  const sortedSeries = [...SERIES_CONFIG].sort((a, b) => {
    const aIsBar = a.chartType === "bar" ? 0 : 1;
    const bIsBar = b.chartType === "bar" ? 0 : 1;
    if (aIsBar !== bIsBar) return aIsBar - bIsBar;
    return a.zIndex - b.zIndex;
  });

  // Draw series (lines and bars)
  sortedSeries.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const seriesData = allSeriesData[cfg.key];
    if (!seriesData || !seriesData.length) return;

    if (cfg.chartType === "bar") {
      // Render as bar chart
      const validPoints = seriesData.filter(
        d => d[0] !== null && d[0] !== undefined && d[1] !== null && d[1] !== undefined
      );
      if (!validPoints.length) return;

      // Calculate bar width based on the median gap between consecutive timestamps
      let barWidth = 6; // default fallback
      if (validPoints.length > 1) {
        const gaps = [];
        for (let i = 1; i < validPoints.length; i++) {
          gaps.push(Math.abs(xScale(validPoints[i][0]) - xScale(validPoints[i - 1][0])));
        }
        gaps.sort((a, b) => a - b);
        const medianGap = gaps[Math.floor(gaps.length / 2)];
        barWidth = Math.max(2, Math.min(medianGap * 0.7, 30)); // 70% of gap, capped at 30px
      }

      chartArea
        .selectAll(`.bar-${cfg.key}`)
        .data(validPoints)
        .enter()
        .append("rect")
        .attr("class", `series-bar series-${cfg.key}`)
        .attr("x", d => xScale(d[0]) - barWidth / 2)
        .attr("y", d => yScale(d[1]))
        .attr("width", barWidth)
        .attr("height", d => Math.max(0, innerHeight - yScale(d[1])))
        .attr("fill", cfg.color)
        .attr("fill-opacity", 0.25)
        .attr("stroke", cfg.color)
        .attr("stroke-opacity", 0.5)
        .attr("stroke-width", 0.5);
    } else {
      // Render as line chart
      const lineGen = line()
        .defined(d => d[0] !== null && d[0] !== undefined && d[1] !== null && d[1] !== undefined)
        .x(d => xScale(d[0]))
        .y(d => yScale(d[1]))
        .curve(cfg.stepLeft ? curveStepAfter : curveLinear);

      const path = chartArea
        .append("path")
        .datum(seriesData)
        .attr("fill", "none")
        .attr("stroke", cfg.color)
        .attr("stroke-width", cfg.lineWidth)
        .attr("d", lineGen)
        .attr("class", `series-line series-${cfg.key}`);

      if (cfg.dashed) {
        path.attr("stroke-dasharray", "6,3");
      }
    }
  });

  // Crosshair
  const crosshair = g
    .append("line")
    .attr("class", "crosshair")
    .attr("y1", 0)
    .attr("y2", innerHeight)
    .attr("stroke", colors.textSecondary)
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2")
    .attr("opacity", 0)
    .attr("pointer-events", "none");

  // Hover markers group
  const markersGroup = g.append("g").attr("class", "hover-markers").attr("pointer-events", "none");

  // Tooltip (must be defined before event handlers)
  const tooltip = select(tooltipEl);

  // Event markers group
  const eventsGroup = g.append("g").attr("class", "events-group").attr("pointer-events", "none");

  // Add event vertical lines with colored circle markers
  if (events && events.length > 0) {
    // Check if events are visible in legend
    const areEventsVisible = visibility["events"] !== false;

    // Map new events to display names for chart visualization
    const mapEventForDisplay = event => {
      const eventName = event.eventName || "Event";
      // Map EVSE Not Ready and EV Not Ready to Charging Stopped for chart display
      if (eventName === "EVSE Not Ready" || eventName === "EV Not Ready") {
        return { ...event, displayName: "Charging Stopped", originalName: eventName };
      }
      return { ...event, displayName: eventName, originalName: eventName };
    };

    if (areEventsVisible) {
      events.forEach(event => {
        const mappedEvent = mapEventForDisplay(event);
        const eventTs = new Date(event.date).getTime();
        const eventX = xScale(eventTs);
        const eventName = mappedEvent.displayName;
        const abbreviation = EVENT_ABBREVIATIONS[eventName] || "?";
        const eventColor = EVENT_COLORS[eventName] || colors.textSecondary;

        // Only draw if within visible range
        if (eventX >= 0 && eventX <= innerWidth) {
          // Add event vertical line
          eventsGroup
            .append("line")
            .attr("x1", eventX)
            .attr("x2", eventX)
            .attr("y1", 0)
            .attr("y2", innerHeight)
            .attr("stroke", eventColor)
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "4,4")
            .attr("opacity", 0.4)
            .attr("pointer-events", "none");

          // Add colored circle marker at the top
          const circleRadius = 8;
          const circleGroup = eventsGroup
            .append("g")
            .attr("class", "event-marker")
            .attr("cursor", "pointer")
            .style("pointer-events", "auto");

          // Circle background
          circleGroup
            .append("circle")
            .attr("cx", eventX)
            .attr("cy", circleRadius + 2)
            .attr("r", circleRadius)
            .attr("fill", eventColor)
            .attr("opacity", 0.9)
            .style("pointer-events", "auto");

          // Abbreviation text inside circle
          circleGroup
            .append("text")
            .attr("x", eventX)
            .attr("y", circleRadius + 4)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("font-size", "9px")
            .attr("font-weight", "700")
            .attr("fill", "#fff")
            .style("pointer-events", "none")
            .text(abbreviation);
        }
      });
    }
  }

  // Brush for x-axis zoom — must be added BEFORE overlay listeners
  const brush = brushX()
    .extent([
      [0, 0],
      [innerWidth, innerHeight],
    ])
    .on("end", event => {
      if (isSyncingRef.current) return;
      if (!event.selection) return;

      const [x0, x1] = event.selection.map(v => xScale.invert(v));

      // Clear brush visually
      g.select(".brush").call(brush.move, null);

      if (onZoomChange) {
        onZoomChange({
          min: x0.getTime(),
          max: x1.getTime(),
          source: "line",
        });
      }
    });

  // Custom filter: only allow brush when shift is NOT pressed
  brush.filter(event => !event.shiftKey && !event.button);

  const brushGroup = g.append("g").attr("class", "brush");
  brushGroup.call(brush);

  // Style brush selection
  brushGroup
    .select(".selection")
    .attr("fill", colors.aiOptimization)
    .attr("fill-opacity", 0.15)
    .attr("stroke", colors.aiOptimization)
    .attr("stroke-width", 1);

  // Use the brush overlay rect for mouse events (tooltip, crosshair, pan)
  const brushOverlay = brushGroup.select(".overlay").style("cursor", "crosshair");

  // Collect all unique timestamps from visible series for snap-to-point behavior
  const allTimestamps = [];
  SERIES_CONFIG.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const sd = allSeriesData[cfg.key];
    if (sd)
      sd.forEach(d => {
        if (d[0] !== null && d[0] !== undefined) allTimestamps.push(d[0]);
      });
  });
  const uniqueTimestamps = [...new Set(allTimestamps)].sort((a, b) => a - b);

  // Mouse event handlers — attached to brush overlay so both brush & tooltip work
  brushOverlay
    .on("mousemove.tooltip", event => {
      const [mx] = pointer(event);
      const rawX = xScale.invert(mx).getTime();

      // Snap to nearest data timestamp only if there's meaningful data
      // If there's only one timestamp (like filled data at 00:00), don't snap - use raw position
      let x = rawX;
      if (uniqueTimestamps.length > 1) {
        let closestTs = uniqueTimestamps[0];
        let minDist = Math.abs(rawX - closestTs);
        for (let i = 1; i < uniqueTimestamps.length; i++) {
          const dist = Math.abs(rawX - uniqueTimestamps[i]);
          if (dist < minDist) {
            minDist = dist;
            closestTs = uniqueTimestamps[i];
          } else {
            break;
          }
        }
        x = closestTs;
      }

      const snappedMx = xScale(x);
      crosshair.attr("x1", snappedMx).attr("x2", snappedMx).attr("opacity", 1);

      // Update hover markers
      markersGroup.selectAll("*").remove();
      SERIES_CONFIG.forEach(cfg => {
        if (!visibility[cfg.name]) return;
        const sd = allSeriesData[cfg.key];
        if (!sd || !sd.length) return;

        let point;
        if (cfg.isSetpoint) {
          const idx = findLeftIndex(sd, x);
          point = idx >= 0 ? sd[idx] : null;
        } else {
          point = findClosestPoint(sd, x);
        }

        if (point && point[0] !== null && point[0] !== undefined && point[1] !== null && point[1] !== undefined) {
          if (cfg.chartType === "bar") {
            // Highlight the hovered bar with a brighter outline
            // Recompute barWidth for consistency with the drawn bars
            const validPts = sd.filter(d => d[0] !== null && d[0] !== undefined && d[1] !== null && d[1] !== undefined);
            let bw = 6;
            if (validPts.length > 1) {
              const gs = [];
              for (let i = 1; i < validPts.length; i++) {
                gs.push(Math.abs(xScale(validPts[i][0]) - xScale(validPts[i - 1][0])));
              }
              gs.sort((a, b) => a - b);
              const mg = gs[Math.floor(gs.length / 2)];
              bw = Math.max(2, Math.min(mg * 0.7, 30));
            }
            markersGroup
              .append("rect")
              .attr("x", xScale(point[0]) - bw / 2 - 1)
              .attr("y", yScale(point[1]) - 1)
              .attr("width", bw + 2)
              .attr("height", Math.max(0, innerHeight - yScale(point[1])) + 1)
              .attr("fill", "none")
              .attr("stroke", cfg.color)
              .attr("stroke-width", 2)
              .attr("stroke-opacity", 1);
          } else {
            markersGroup
              .append("circle")
              .attr("cx", xScale(point[0]))
              .attr("cy", yScale(point[1]))
              .attr("r", 4)
              .attr("fill", cfg.color)
              .attr("stroke", isDark ? "#1e293b" : "#fff")
              .attr("stroke-width", 1.5);
          }
        }
      });

      // Position tooltip
      const tooltipHTML = buildTooltipHTML(x, allSeriesData, visibility, siteTimezone, events);
      tooltip.html(tooltipHTML).style("opacity", 1).style("pointer-events", "none");

      const tooltipWidth = tooltipEl?.offsetWidth || CHART_CONSTANTS.TOOLTIP_DEFAULT_WIDTH;
      const tooltipHeight = tooltipEl?.offsetHeight || CHART_CONSTANTS.TOOLTIP_DEFAULT_HEIGHT;
      const containerRect = container.getBoundingClientRect();
      let left = event.clientX - containerRect.left + CHART_CONSTANTS.TOOLTIP_OFFSET_X;
      let top = event.clientY - containerRect.top - tooltipHeight / 2;

      // Flip tooltip if going off right edge
      if (left + tooltipWidth > containerRect.width - CHART_CONSTANTS.TOOLTIP_EDGE_PADDING) {
        left = event.clientX - containerRect.left - tooltipWidth - CHART_CONSTANTS.TOOLTIP_OFFSET_X;
      }
      if (top < 0) top = CHART_CONSTANTS.TOOLTIP_TOP_PADDING;
      if (top + tooltipHeight > containerRect.height) {
        top = containerRect.height - tooltipHeight - TOOLTIP_BOTTOM_PADDING;
      }

      tooltip.style("left", `${left}px`).style("top", `${top}px`);
    })
    .on("mouseleave.tooltip", () => {
      crosshair.attr("opacity", 0);
      markersGroup.selectAll("*").remove();
      tooltip.style("opacity", 0);
    });

  // Pan with shift+drag
  let panStart = null;
  let panDomainStart = null;

  brushOverlay.on("mousedown.pan", event => {
    if (event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      panStart = pointer(event)[0];
      panDomainStart = xScale.domain().map(d => d.getTime());
      brushOverlay.style("cursor", "grabbing");

      const onMouseMove = e => {
        if (panStart === null || panStart === undefined) return;
        const dx = pointer(e, svgEl)[0] - MARGIN.left - panStart;
        const domainWidth = panDomainStart[1] - panDomainStart[0];
        const pixelWidth = innerWidth;
        const shift = -(dx / pixelWidth) * domainWidth;

        const newMin = panDomainStart[0] + shift;
        const newMax = panDomainStart[1] + shift;

        if (onZoomChange && !isSyncingRef.current) {
          onZoomChange({ min: newMin, max: newMax, source: "line" });
        }
      };

      const onMouseUp = () => {
        panStart = null;
        panDomainStart = null;
        brushOverlay.style("cursor", "crosshair");
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      // Store cleanup so the React component can remove listeners on unmount
      if (panCleanupRef) {
        panCleanupRef.current = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
        };
      }
    }
  });

  // Double click to reset zoom
  brushOverlay.on("dblclick.reset", () => {
    if (onZoomChange) {
      onZoomChange(null);
    }
  });
}
